import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { BarChart } from '@/charts/BarChart'
import { TrendChart } from '@/charts/TrendChart'
import { SERIES, formatValue } from '@/charts/palette'
import { usePageLoader } from '@/components/LogoLoader'
import { StatTile } from '@/components/StatTile'
import { tidyLabel } from '@/features/aufmass/types'
import { fmtNumber, useT } from '@/i18n'
import { crewsMessages } from '@/i18n/messages/crews'
import { isoWeek, type CrewPerformance } from './types'

const MIN_RANKED_DAYS = 5

/**
 * How the crew performs: output against the company, quality, where it ranks and what it
 * builds. Report days only (see CrewInsightsQuery) — days with no filed report are shown as a
 * quality figure instead of silently dragging the averages down.
 */
export function PerformanceTab({ crewId }: { crewId: string }) {
  const t = useT(crewsMessages)
  const { data, isPending, error } = useQuery<CrewPerformance>({
    queryKey: ['crew-performance', crewId],
    queryFn: () => api.get<CrewPerformance>(`/api/crews/${crewId}/performance`),
    staleTime: 5 * 60_000,
  })

  const loader = usePageLoader(isPending, t('loadingPerformance'))
  if (loader) return loader
  if (error || !data) return <p className="text-sm" style={{ color: 'var(--viz-critical)' }}>{t('notFound')}</p>

  const { quality: q, ranking: r } = data
  const vsCompany = data.companyMetresPerDay > 0 && data.reportDays > 0
    ? ((data.metresPerDay - data.companyMetresPerDay) / data.companyMetresPerDay) * 100
    : undefined
  const perHour = data.manHours > 0 ? data.metres / data.manHours : 0
  const fixPct = q.segments > 0 ? (q.withFix / q.segments) * 100 : null

  return (
    <div className="space-y-4">
      <p className="text-xs text-neutral-500">{t('perfWindow', { days: data.windowDays })}</p>

      <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
        <StatTile label={t('statMetresPerDay')} value={Math.round(data.metresPerDay)} unit="m"
          {...(vsCompany != null ? { changePct: vsCompany } : {})} compareLabel={t('vsCompany')} />
        <StatTile label={t('statMetres')} value={data.metres} unit="m" />
        <StatTile label={t('statReportDays')} value={data.reportDays} unit="" />
        <StatTile label={t('statMetresPerHour')} value={perHour} unit="m" display={`${fmtNumber(perHour, 1)} m`} />
      </div>

      {data.reportDays === 0 && (
        <p className="rounded-2xl bg-neutral-100 p-3 text-sm text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
          {t('noReportsInWindow', { days: data.windowDays })}
        </p>
      )}

      <TrendChart
        title={t('trendTitle')}
        subtitle={t('trendSubtitle')}
        unit="m"
        color={SERIES.primary}
        valueLabel={t('trendThisCrew')}
        referenceLabel={t('trendCompany')}
        points={data.weeks.map((w) => ({
          label: `KW ${isoWeek(w.weekStart)}`,
          value: w.crewDays > 0 ? Math.round(w.metres / w.crewDays) : 0,
          reference: Math.round(w.companyMetresPerDay),
        }))}
      />

      <div className="grid gap-3 lg:grid-cols-2">
        <Panel title={t('rankTitle')} hint={t('rankHint', { count: r.crews, min: MIN_RANKED_DAYS, days: data.windowDays })}>
          <div className="grid grid-cols-2 gap-2">
            <Rank label={t('rankMetresPerDay')} rank={r.metresPerDay} of={r.crews} />
            <Rank label={t('rankMetres')} rank={r.metres} of={r.crews} />
            <Rank label={t('rankDays')} rank={r.days} of={r.crews} />
            <Rank label={t('rankFix')} rank={r.fix} of={r.rtkCrews} />
          </div>
        </Panel>

        <Panel title={t('qualityTitle')}>
          {fixPct == null ? (
            <p className="rounded-xl bg-neutral-100 p-3 text-sm text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
              ⌀ {t('qualityNoRtk')}
            </p>
          ) : (
            <div>
              <div className="flex items-baseline justify-between gap-2 text-sm">
                <span>{t('qualityRtkFix')}</span>
                <span className="tabular font-semibold">{fmtNumber(fixPct, 0)}%</span>
              </div>
              <div className="mt-1 h-2 rounded-full bg-neutral-100 dark:bg-neutral-800">
                <div className="h-full rounded-full" style={{ width: `${fixPct}%`, backgroundColor: fixPct >= 90 ? 'var(--viz-good)' : fixPct >= 60 ? 'var(--viz-warning)' : 'var(--viz-serious)' }} />
              </div>
              <div className="mt-1 flex justify-between text-xs text-neutral-500">
                <span>{t('qualityOfSegments', { fix: q.withFix, total: q.segments })}</span>
                {q.meanAccuracy != null && <span>{t('qualityAccuracy')}: <span className="tabular">{fmtNumber(q.meanAccuracy * 100, 1)} cm</span></span>}
              </div>
            </div>
          )}
          <dl className="mt-3 grid grid-cols-3 gap-2 text-center">
            <Figure label={t('qualityRepairs')} value={q.repairDays} />
            <Figure label={t('qualityFlagged')} value={q.flaggedDays} hint={t('qualityFlaggedHint')} warn={q.flaggedDays > 0} />
            <Figure label={t('qualityNoReport')} value={q.noReportDays} hint={t('qualityNoReportHint')} warn={q.noReportDays > q.reportDays / 2} />
          </dl>
        </Panel>
      </div>

      {data.positions.length > 0 && (
        <BarChart
          title={t('buildTitle')}
          subtitle={t('buildSubtitle', { days: data.windowDays })}
          orientation="bar"
          unit=""
          color={SERIES.primary}
          bars={data.positions.map((p) => ({ label: tidyLabel(p.name), value: Number(p.value), note: formatValue(p.days, '') }))}
        />
      )}
    </div>
  )
}

function Panel({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="rounded-2xl bg-white p-4 ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
      <h3 className="text-sm font-semibold">{title}</h3>
      {hint && <p className="mt-0.5 text-xs text-neutral-500">{hint}</p>}
      <div className="mt-3">{children}</div>
    </section>
  )
}

function Rank({ label, rank, of }: { label: string; rank: number | null; of: number }) {
  const t = useT(crewsMessages)
  // Top quarter green, bottom quarter amber: a glance, not a grade.
  const tone = rank == null ? 'text-neutral-400'
    : rank <= Math.ceil(of / 4) ? 'text-green-600 dark:text-green-400'
    : rank > of - Math.ceil(of / 4) ? 'text-amber-600 dark:text-amber-400'
    : 'text-neutral-900 dark:text-neutral-100'
  return (
    <div className="rounded-xl bg-neutral-50 p-3 dark:bg-neutral-800/50">
      <div className="text-xs text-neutral-500">{label}</div>
      {rank == null ? (
        <div className="mt-1 text-sm text-neutral-400">{t('notRanked')}</div>
      ) : (
        <div className="mt-0.5">
          <span className={`tabular text-2xl font-semibold ${tone}`}>#{rank}</span>
          <span className="ml-1 text-xs text-neutral-500">{t('rankOf', { count: of })}</span>
        </div>
      )}
    </div>
  )
}

function Figure({ label, value, hint, warn }: { label: string; value: number; hint?: string; warn?: boolean }) {
  return (
    <div className="rounded-xl bg-neutral-50 p-2 dark:bg-neutral-800/50" title={hint}>
      <dd className={`tabular text-lg font-semibold ${warn ? 'text-amber-600 dark:text-amber-400' : ''}`}>{value}</dd>
      <dt className="text-[11px] leading-tight text-neutral-500">{label}</dt>
    </div>
  )
}
